// @flow
// later
/* eslint-disable ta-code-conventions/has-matching-test, ta-code-conventions/has-matching-storydoc */
/* eslint-disable prefer-destructuring */

import * as React from 'react';
import classNames from '@ta/common.classnames';
import { unescaped } from '@ta/common.i18n';
import { type JsonLdBreadcrumbData, BreadcrumbsJsonLd } from '@ta/common.json-ld';
import Setable from '@ta/common.setable';
import type { SetableSetFunc } from '@ta/common.setable';

import type { NavMenu, Breadcrumb } from './types';
import { handleClick } from './handlers';
import styles from './MobileFooterMenus.less';

type ToggleProps = {|
  checked: boolean,
|};

const getMenuHref = (menu: NavMenu) => {
  if (menu.action) {
    if (menu.action.href) {
      return menu.action.href;
    }
    if (menu.action.type === 'URL' && menu.action.data) {
      return menu.action.data;
    }
  }
  return `${'javascript'}${':void'}`;
};

const getMenuHandler = (menu: NavMenu) => {
  if (!menu.action || !menu.action.data) {
    return null; // default anchor action
  }
  const action = menu.action;
  return ev => handleClick(ev, action);
};

const getMenuLabel = (menu: NavMenu) => {
  if (menu.label === '...') {
    return <span className="ui_icon more-horizontal" />;
  }
  return unescaped(menu.label);
};

const renderItem = (menu: NavMenu) => {
  if (menu.items && menu.items.length) {
    return (
      <li className="tabs_sublist_item with_sub" key={`${menu.label}.${menu.icon || ''}`}>
        <Setable childProps={{ checked: false }}>
          {(tp: ToggleProps, setFunc: SetableSetFunc<ToggleProps>) => (
            <React.Fragment>
              <input type="checkbox" checked={tp.checked} className="toggle" readOnly />
              <span
                className="tabs_sublist_text"
                onClick={() => {
                  setFunc({ checked: !tp.checked });
                }}
              >
                {getMenuLabel(menu)}
              </span>
            </React.Fragment>
          )}
        </Setable>
        <div className="ui_icon caret-down" />
        <ul className="tabs_sublist">{menu.items.map(renderItem)}</ul>
      </li>
    );
  }
  return (
    <li className={`tabs_sublist_item ${styles.link}`} key={`${menu.label}.${menu.icon || ''}`}>
      <a href={getMenuHref(menu)} className="tabs_sublist_text" onClick={getMenuHandler(menu)}>
        {getMenuLabel(menu)}
      </a>
    </li>
  );
};

const renderMenu = (menu: NavMenu) => {
  if (menu.items && menu.items.length) {
    return (
      <li className={`tabs_item with_sub ${styles.item}`} key={`${menu.label}.${menu.icon || ''}`}>
        <Setable childProps={{ checked: false }}>
          {(tp: ToggleProps, setFunc: SetableSetFunc<ToggleProps>) => (
            <React.Fragment>
              <input type="checkbox" checked={tp.checked} className="toggle" readOnly />
              <span
                className="tabs_text"
                onClick={() => {
                  setFunc({ checked: !tp.checked });
                }}
              >
                {getMenuLabel(menu)}
              </span>
            </React.Fragment>
          )}
        </Setable>
        <div className="ui_icon caret-down" />
        <ul className="tabs_sublist">{menu.items.map(renderItem)}</ul>
      </li>
    );
  }
  return (
    <li className={`tabs_item ${styles.item}`} key={`${menu.label}.${menu.icon || ''}`}>
      <a href={getMenuHref(menu)} className="tabs_text" onClick={getMenuHandler(menu)}>
        {getMenuLabel(menu)}
      </a>
    </li>
  );
};

const crumbClick = (ev: SyntheticEvent<*>, crumb: Breadcrumb) => {
  if (crumb.href) {
    handleClick(ev, {
      type: 'URL',
      data: crumb.href,
      trackingCategory: 'Breadcrumbs',
      trackingLabel: crumb.placeType || '',
      trackingValue: (crumb.crumbIndex && Number(crumb.crumbIndex)) || 0,
    });
  }
};

type BreadcrumbRenderProps = {|
  crumb: Breadcrumb,
  compactDesign: ?boolean,
  lastItemTreatment: boolean,
|};

const renderBreadcrumb = (crumbProps: BreadcrumbRenderProps) =>
  crumbProps.crumb && (
    <li
      className={classNames(
        'tabs_item',
        styles.item,
        crumbProps.compactDesign ? styles.compactDesignItem : '',
      )}
      itemScope=""
      key={crumbProps.crumb.name}
    >
      <a
        href={crumbProps.crumb.href}
        className={classNames(
          'tabs_text',
          styles.link,
          crumbProps.lastItemTreatment ? styles.compactDesignLastItem : '',
        )}
        onClick={e => crumbClick(e, crumbProps.crumb)}
      >
        {unescaped(crumbProps.crumb.name)}
      </a>
    </li>
  );

/** MobileFooterMenus Props */
export type Props = {|
  /** Header text (not HTML) */
  headerText: ?string,
  /** Breadcrumbs */
  breadcrumbs: ?Array<Breadcrumb>,
  /** Nav menus */
  menus: ?Array<NavMenu>,
  /** A secondary design which looks more compact in terms of white space */
  compactDesign: ?boolean,
  /** Optional overrides for list style */
  listClassName?: string,
  /** Optional overrides for header style */
  headerClassName?: string,
|};

const MobileFooterMenus = (props: Props) => {
  const jsonLdBreadcrumbs: JsonLdBreadcrumbData[] = (props.breadcrumbs || [])
    .map(crumb => ({ name: crumb.name, href: crumb.href }))
    .reverse();
  return (
    <>
      <div
        className={classNames(
          'is-hidden-tablet',
          'ui_collapsible_list',
          props.listClassName ? props.listClassName : styles.list,
          props.compactDesign ? styles.compactDesignList : '',
        )}
      >
        {!props.headerText ? null : (
          <h1
            className={classNames(
              props.headerClassName ? props.headerClassName : styles.header,
              'masthead', // keep the smoke tests happy
              props.compactDesign ? styles.compactDesignHeader : '',
            )}
          >
            {props.headerText}
          </h1>
        )}
        <ul className={classNames(!props.compactDesign ? 'tabs' : styles.compactDesignTabs)}>
          {(props.breadcrumbs || []).map((breadcrumb, index) =>
            renderBreadcrumb({
              crumb: breadcrumb,
              lastItemTreatment:
                (props.compactDesign &&
                  (!props.menus || props.menus.length === 0) &&
                  (props.breadcrumbs && index === props.breadcrumbs.length - 1)) ||
                false,
              compactDesign: props.compactDesign,
            }),
          )}
          {(props.menus || []).map(renderMenu)}
        </ul>
      </div>
      <BreadcrumbsJsonLd breadcrumbList={jsonLdBreadcrumbs} />
    </>
  );
};

MobileFooterMenus.defaultProps = {
  compactDesign: false,
};

export default MobileFooterMenus;
